import { ApiResponse } from 'apisauce';

import { ErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { ResponseModel } from '../../../shared/interfaces/response-model.interface';
import {
  OverduePaymentsFilterOptionsModel,
  OverduePaymentsModel,
  OverduePaymentsResponseModel,
  OverduePaymentsSearchModel
} from '../../../shared/models/overdue-payments.model';
import api from '../../../shared/services/utils/api/api.service';
import errorHelper from '../../../shared/services/utils/api/error-helper.service';

const MODULE_NAME = 'PAYMENTS_MODULE';

/** A successful export: the CSV body, plus the headers that carry its server-chosen filename. */
export interface OverduePaymentsExport {
  file: Blob | string;
  headers?: any;
}

const EMPTY_TOTALS = {
  payments: 0,
  reservations: 0,
  remainingEgp: 0,
  remainingUsd: 0,
  remainingEgpEquivalent: 0
};

export function getOverduePayments(
  query: OverduePaymentsSearchModel
): Promise<OverduePaymentsModel | ErrorInfo> {
  return api
    .getOverduePayments(query)
    .then((response: ApiResponse<OverduePaymentsResponseModel, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data && data.data) {
          return {
            rows: data.data,
            count: data.count || 0,
            totals: data.totals || EMPTY_TOTALS,
            truncated: Boolean(data.truncated)
          };
        }

        return errorHelper(0, '', MODULE_NAME, (api.getOverduePayments as any).displayName);
      }

      return errorHelper(
        Number(response.status),
        response.data && response.data.errorCode
          ? String(response.data.errorCode || '')
          : response.problem,
        MODULE_NAME,
        (api.getOverduePayments as any).displayName
      );
    });
}

/**
 * The export comes back as a CSV body, not JSON, so the payload is returned for the caller to turn
 * into a download. A failure still arrives as JSON, which is why the ok check comes first.
 *
 * The response headers come back with it so the caller can hand them to triggerBrowserDownload,
 * which prefers the filename the endpoint set in Content-Disposition over any name the client
 * would guess.
 */
export function exportOverduePayments(
  query: OverduePaymentsSearchModel
): Promise<OverduePaymentsExport | ErrorInfo> {
  return api.exportOverduePayments(query).then((response: ApiResponse<any, ErrorInfo>) => {
    if (response.ok && response.data) {
      return { file: response.data as Blob | string, headers: response.headers };
    }

    // `data` is typed loosely here because a successful export is a CSV body, so the error branch
    // gets no narrowing and `problem` stays nullable — unlike the JSON call above.
    return errorHelper(
      Number(response.status),
      response.data && response.data.errorCode
        ? String(response.data.errorCode || '')
        : String(response.problem || ''),
      MODULE_NAME,
      (api.exportOverduePayments as any).displayName
    );
  });
}

export function getOverduePaymentsFilterOptions(): Promise<
  OverduePaymentsFilterOptionsModel | ErrorInfo
> {
  return api
    .getOverduePaymentsFilterOptions()
    .then((response: ApiResponse<ResponseModel<OverduePaymentsFilterOptionsModel>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data && data.data) {
          return data.data;
        }

        return errorHelper(
          0,
          '',
          MODULE_NAME,
          (api.getOverduePaymentsFilterOptions as any).displayName
        );
      }

      return errorHelper(
        Number(response.status),
        response.data && response.data.errorCode
          ? String(response.data.errorCode || '')
          : response.problem,
        MODULE_NAME,
        (api.getOverduePaymentsFilterOptions as any).displayName
      );
    });
}
